import { StatusList } from 'src/types/statusTypes';

const normalizeValue = (value) =>
  value === null || value === undefined ? '' : String(value).trim();

const hasReturnCargoInfo = (post) =>
  !!(
    normalizeValue(post?.returnCargoFirm) ||
    normalizeValue(post?.returnDeliveryCode) ||
    normalizeValue(parsePostDataJSON(post)?.returnCargoFirm) ||
    normalizeValue(parsePostDataJSON(post)?.returnCargoTrackingNumber)
  );

export const parsePostDataJSON = (post) => {
  if (!post?.dataJSON) return null;

  if (typeof post.dataJSON === 'object') {
    return post.dataJSON;
  }

  try {
    return JSON.parse(post.dataJSON);
  } catch (error) {
    return null;
  }
};

export const getShopifyDomainLabel = (post) => {
  const parsedData = parsePostDataJSON(post);
  const domainFromData = parsedData?.domain?.trim();
  if (domainFromData) return domainFromData;

  const title = String(post?.title || '').trim();
  const match = title.match(/^shopify\s*-\s*([^\s]+)\s+/i);
  if (match?.[1]) return match[1].trim();

  return 'Shopify';
};

export const getCarrierLabel = (post) => {
  if (post?.apiUserType === 'kargoist') return 'CargoX';
  if (post?.apiUserType === 'shopify') {
    return getShopifyDomainLabel(post);
  }

  return post?.apiUserType || '-';
};

export const getStatusLabelByValue = (statusValue) => {
  const status = StatusList.find((item) => item.id === +normalizeValue(statusValue));
  return status ? status.label : '';
};

export const isShopifyDropOffReturnPending = (post) =>
  normalizeValue(post?.apiUserType) === 'shopify' &&
  normalizeValue(post?.isDropOff) === '1' &&
  normalizeValue(post?.status) === '5' &&
  hasReturnCargoInfo(post);

export const isShopifyStoreReturn = (post) =>
  normalizeValue(post?.apiUserType) === 'shopify' &&
  normalizeValue(post?.isDropOff) === '0' &&
  ['damaged', 'fraud'].includes(normalizeValue(post?.return_reason).toLowerCase());

export const getEffectiveStatusValue = (post) =>
  isShopifyDropOffReturnPending(post) ? '9' : normalizeValue(post?.status);

export const getStatusLabel = (post) =>
  getStatusLabelByValue(getEffectiveStatusValue(post)) || '-';

export const getPostTimelineValue = (post) => {
  const candidates = [
    post?.last_update_date_time,
    post?.completedDateTime,
    post?.returnDateTime,
    post?.deliveredDateTime,
    post?.inBranchDateTime,
    post?.cancelledDateTime,
    post?.createdDateTime,
    post?.numeric_index,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return candidates.length ? Math.max(...candidates) : 0;
};

export const getPreferredPostFromResults = (results = []) => {
  if (!Array.isArray(results) || results.length === 0) return null;

  const sortedResults = [...results].sort((a, b) => {
    const timelineDiff = getPostTimelineValue(b) - getPostTimelineValue(a);
    if (timelineDiff !== 0) return timelineDiff;

    const numericIndexDiff =
      Number(b?.numeric_index || 0) - Number(a?.numeric_index || 0);
    if (numericIndexDiff !== 0) return numericIndexDiff;

    return Number(b?.id || 0) - Number(a?.id || 0);
  });

  return sortedResults[0];
};

export const canTakePost = (post) =>
  (normalizeValue(post?.status) === '3' &&
    !['handsfree', 'aras-kargo', 'surat-kargo'].includes(
      normalizeValue(post?.apiUserType)
    )) ||
  (normalizeValue(post?.status) === '46' &&
    normalizeValue(post?.apiUserType) === 'surat-kargo') ||
  (post?.dataEntranceType === 'Easypoint APP API' &&
    normalizeValue(post?.status) === '3');

export const canDeliverSuratKargoPost = (post) =>
  normalizeValue(post?.apiUserType) === 'surat-kargo' &&
  getEffectiveStatusValue(post) === '9' &&
  normalizeValue(post?.isDropOff) === '0';

export const canSetInBranchPost = (post) =>
  (normalizeValue(post?.status) === '4' &&
    normalizeValue(post?.apiUserType) !== 'aras-kargo') ||
  (normalizeValue(post?.status) === '33' &&
    normalizeValue(post?.apiUserType) === 'hepsijet');

export const canReturnPost = (post) =>
  getEffectiveStatusValue(post) === '9' &&
  normalizeValue(post?.apiUserType) !== 'trendyol' &&
  normalizeValue(post?.apiUserType) !== 'surat-kargo';

export const requiresReturnLabelConfirmation = (post) =>
  getEffectiveStatusValue(post) === '9' &&
  ['hepsiburada', 'shopify'].includes(normalizeValue(post?.apiUserType));

export const getReturnConfirmationReference = (post) =>
  post?.apiUserType === 'hepsiburada' &&
  (post?.isDropOff === '1' || ['9', '16', '21'].includes(post?.status))
    ? post?.barcode || post?.orderNumber || post?.apiOrderID
    : post?.orderNumber || post?.apiOrderID || post?.barcode;

export const shouldShowReturnCargoFields = (post) => {
  if (!post) return false;
  if (normalizeValue(post?.apiUserType) !== 'shopify') return true;
  if (normalizeValue(post?.isDropOff) === '1') return true;

  const effectiveStatus = String(getEffectiveStatusValue(post) || '');
  return ['9', '45'].includes(effectiveStatus);
};

export const getScenarioChips = (post) => {
  const chips = [];

  if (post?.isDropOff === '1') {
    chips.push({
      label: 'Drop Off',
      sx: {
        color: '#B54708',
        backgroundColor: '#FFF7ED',
        borderColor: '#FED7AA',
      },
    });
  }

  if (post?.dataEntranceType === 'Easypoint APP API') {
    chips.push({
      label: 'Easypoint App API',
      sx: {
        color: '#175CD3',
        backgroundColor: '#EFF8FF',
        borderColor: '#B2DDFF',
      },
    });
  }

  return chips;
};

export const getStatusBadgeSx = (post) => {
  const statusLabel = getStatusLabel(post)?.toLocaleLowerCase('tr') || '';

  if (statusLabel.includes('bek')) {
    return {
      color: '#B54708',
      backgroundColor: '#FFF7ED',
      border: '1px solid #FED7AA',
    };
  }

  if (statusLabel.includes('şube') || statusLabel.includes('zimmet')) {
    return {
      color: '#175CD3',
      backgroundColor: '#EFF8FF',
      border: '1px solid #B2DDFF',
    };
  }

  if (statusLabel.includes('iade')) {
    return {
      color: '#B42318',
      backgroundColor: '#FEF3F2',
      border: '1px solid #FECDCA',
    };
  }

  if (statusLabel.includes('tamam') || statusLabel.includes('teslim')) {
    return {
      color: '#067647',
      backgroundColor: '#ECFDF3',
      border: '1px solid #ABEFC6',
    };
  }

  return {
    color: '#344054',
    backgroundColor: '#F2F4F7',
    border: '1px solid #D0D5DD',
  };
};
